import React from "react";

import {
  TextField,
  Chip,
  FormControl,
  Autocomplete,
  AutocompleteRenderInputParams,
  FilterOptionsState,
} from "@mui/material";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

interface IndividualEmailsProps {
  individualEmails: string[];
  setIndividualEmails: (emails: string[]) => void;
}

const IndividualEmails: React.FC<IndividualEmailsProps> = ({
  individualEmails,
  setIndividualEmails,
}) => {
  const { t } = useTranslation("bulletin/share");

  // Validate email format
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <div>
      <FormControl fullWidth>
        <Autocomplete<string, true, false, true>
          multiple
          id="email-autocomplete"
          options={[]}
          value={individualEmails}
          freeSolo
          color="secondary"
          autoSelect
          size="small"
          onChange={(event, newValue) => {
            // Check if a new email is being added
            if (newValue.length > individualEmails.length) {
              // Handle newly added email
              const lastValue = newValue[newValue.length - 1];
              if (typeof lastValue === "string") {
                // Check if the string is in the "click or press enter to add" format
                const emailMatch = lastValue.match(
                  /@([a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/,
                );
                let email = lastValue.trim();

                // If it's a prompt format string, extract the actual email address
                if (
                  emailMatch &&
                  lastValue.includes(
                    t("press_enter_to_add_email", { email: "" }).split("")[0],
                  )
                ) {
                  // last value is a string like "click or press enter to add 'email@example.com'"
                  email = lastValue.split("'")[1];
                }
                if (email && isValidEmail(email)) {
                  // Case-insensitive duplicate check
                  const lowerCaseEmails = individualEmails.map((e) =>
                    e.toLowerCase(),
                  );
                  if (lowerCaseEmails.includes(email.toLowerCase())) {
                    toast.warning(
                      t("duplicate_email") || "Duplicate email address",
                    );
                    // Keep the existing emails unchanged if duplicate
                    setIndividualEmails(individualEmails);
                  } else {
                    // Add new valid email
                    setIndividualEmails([...individualEmails, email]);
                  }
                } else if (email) {
                  toast.warning(
                    t("invalid_email_format") || "Invalid email format",
                  );
                  // Keep the existing emails unchanged if invalid
                  setIndividualEmails(individualEmails);
                }
              }
            } else {
              // Handle delete operation - use the new value directly
              setIndividualEmails(newValue);
            }
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  variant="outlined"
                  label={option}
                  {...tagProps}
                />
              );
            })
          }
          filterOptions={(
            options: string[],
            params: FilterOptionsState<string>,
          ) => {
            const { inputValue } = params;
            // Only suggest when there's input and it looks like an email
            if (inputValue !== "") {
              const trimmedInput = inputValue.trim();
              return [
                `${t("press_enter_to_add_email", { email: trimmedInput })}`,
              ];
            }
            return [];
          }}
          getOptionLabel={(option: string) => option}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              color="secondary"
              label={t("individual_emails") || "Email Address"}
              variant="outlined"
            />
          )}
          renderOption={(props, option) => <li {...props}>{option}</li>}
        />
      </FormControl>
    </div>
  );
};

export default IndividualEmails;
